import { useEffect, useRef } from 'react';

function generateSessionId() {
	return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export default function useLeadCaptureTracking({
	userId,
	orgId,
	homeId,
	templateId,
	address,
	city,
	sectionRefs = {}
}) {
	const sessionIdRef = useRef(generateSessionId());
	const sessionStartRef = useRef(Date.now());
	const lastActivityRef = useRef(Date.now());
	const viewedSectionsRef = useRef(new Set());
	const bufferRef = useRef([]);
	const API_ENDPOINT = '/api/lead-capture/track-interaction';
	const SNAPSHOT_KEY = 'leadCaptureTrackingSnapshot';

	const flushBuffer = (mode = 'normal') => {
		if (!bufferRef.current.length) return;
		const payload = {
			sessionId: sessionIdRef.current,
			userId,
			orgId,
			homeId,
			templateId,
			address,
			city,
			sessionStart: sessionStartRef.current,
			events: bufferRef.current,
			viewedSections: Array.from(viewedSectionsRef.current)
		};
		bufferRef.current = [];
		if (mode === 'beacon' && navigator.sendBeacon) {
			const blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
			navigator.sendBeacon(API_ENDPOINT, blob);
		} else {
			fetch(API_ENDPOINT, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(payload),
				keepalive: mode !== 'normal'
			}).catch(() => {});
		}
	};

	const saveSnapshot = () => {
		const snapshot = {
			sessionId: sessionIdRef.current,
			events: bufferRef.current,
			viewedSections: Array.from(viewedSectionsRef.current),
			meta: {
				userId,
				orgId,
				homeId,
				templateId,
				address,
				city,
				sessionStart: sessionStartRef.current
			}
		};
		localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
	};

	const restoreSnapshot = () => {
		const raw = localStorage.getItem(SNAPSHOT_KEY);
		if (!raw) return;
		try {
			const snap = JSON.parse(raw);
			if (snap.events && snap.events.length) {
				bufferRef.current.push(...snap.events);
				flushBuffer('normal');
			}
		} catch (e) {}
		localStorage.removeItem(SNAPSHOT_KEY);
	};

	const trackEvent = (type, details = {}) => {
		const evt = { type, details, ts: Date.now() };
		bufferRef.current.push(evt);
		lastActivityRef.current = Date.now();
	};

	useEffect(() => {
		restoreSnapshot();

		trackEvent('page_view');
		flushBuffer('normal');

		const clickHandler = (e) => {
			const target = e.target;
			const name =
				target.getAttribute('data-track') ||
				target.id ||
				target.className ||
				target.tagName;
			trackEvent('click', { name });
		};
		document.addEventListener('click', clickHandler);

		const observers = [];
		const attachedKeys = new Set();

		const attachObservers = () => {
			Object.entries(sectionRefs).forEach(([key, ref]) => {
				if (attachedKeys.has(key) || !ref || !ref.current) return;
				const observer = new IntersectionObserver(
					(entries) => {
						if (entries[0].isIntersecting && !viewedSectionsRef.current.has(key)) {
							viewedSectionsRef.current.add(key);
							trackEvent('view_section', { section: key });
						}
					},
					{ threshold: 0.3 }
				);
				observer.observe(ref.current);
				observers.push(observer);
				attachedKeys.add(key);
			});
		};

		attachObservers();
		const observerPoll = setInterval(attachObservers, 2000);

		const periodicSend = setInterval(() => {
			if (bufferRef.current.length) flushBuffer('normal');
		}, 10000);

		const snapshotSave = setInterval(() => {
			saveSnapshot();
		}, 30000);

		const handleBeforeUnload = () => {
			saveSnapshot();
			flushBuffer('beacon');
		};
		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			document.removeEventListener('click', clickHandler);
			observers.forEach((obs) => obs.disconnect());
			clearInterval(observerPoll);
			clearInterval(periodicSend);
			clearInterval(snapshotSave);
			window.removeEventListener('beforeunload', handleBeforeUnload);
			flushBuffer('beacon');
		};
	}, [userId, orgId, homeId, templateId, address, city, sectionRefs]);

	return trackEvent;
}
